import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  Chip,
  Stack,
  TextField,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';
import { actions } from './globalReducer';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const STATUS_BDS_TYPE = {
  completed: 'staticSuccess',
  failed: 'errorNeutral',
  partial: 'warningNeutral',
  processing: 'staticNeutral',
};

const ACTION_BDS_TYPE = {
  allow: 'staticSuccess',
  block: 'errorNeutral',
  monitor: 'staticNeutral',
};

const ScanLookupContainer = () => {
  const tokens = useTokens();
  const dispatch = useDispatch();
  const { isLoading, data, error } = useSelector((state) => state.global.scanLookup);

  const [correlationId, setCorrelationId] = useState('');
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(actions.clearScanLookup());
    };
  }, [dispatch]);

  const handleSearch = () => {
    if (correlationId.trim()) {
      setSearched(true);
      dispatch(actions.lookupScan(correlationId.trim()));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Box sx={{ padding: tokens.aliasesSpaceContainerSpacious }}>
      <Stack
        sx={{
          gap: tokens.aliasesSpaceComponentDense,
          marginBottom: tokens.aliasesSpaceContainerDefault,
        }}
      >
        <Typography variant="h2">Scan lookup</Typography>
        <Typography variant="captionSubtle">
          Look up a scan by correlation ID to view full details.
        </Typography>
      </Stack>

      <Stack
        direction="row"
        sx={{
          gap: tokens.aliasesSpaceComponentDefault,
          marginBottom: tokens.aliasesSpaceContainerDefault,
          alignItems: 'flex-start',
        }}
      >
        <TextField
          label="Correlation ID"
          value={correlationId}
          onChange={(e) => setCorrelationId(e.target.value)}
          onKeyPress={handleKeyPress}
          bdsSize="small"
          placeholder="e.g., abc123-def456-..."
          sx={{ minWidth: 400 }}
        />
        <Button
          bdsType="interactiveEmphasis"
          onClick={handleSearch}
          disabled={!correlationId.trim() || isLoading}
          isLoading={isLoading}
        >
          Search
        </Button>
      </Stack>

      {error && (
        <Typography
          variant="body1"
          sx={{
            color: tokens.aliasesColorTextError,
            marginBottom: tokens.aliasesSpaceContainerDefault,
          }}
        >
          {error}
        </Typography>
      )}

      {!searched && !data && (
        <Typography variant="captionSubtle">
          Enter a correlation ID to look up scan details.
        </Typography>
      )}

      {searched && !isLoading && !data && !error && (
        <Typography variant="captionSubtle">No scan found with that correlation ID.</Typography>
      )}

      {data && (
        <Card bdsType="staticSubtle" bdsPadding="spacious" bdsBoxShadow="default">
          <Stack sx={{ gap: tokens.aliasesSpaceContainerDefault }}>
            <Stack
              direction="row"
              sx={{
                gap: tokens.aliasesSpaceComponentDefault,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Chip
                label={data.status}
                bdsType={STATUS_BDS_TYPE[data.status] || 'staticNeutral'}
                bdsSize="medium"
              />
              {data.result_action && (
                <Chip
                  label={data.result_action}
                  bdsType={ACTION_BDS_TYPE[data.result_action] || 'staticNeutral'}
                  bdsSize="medium"
                />
              )}
            </Stack>

            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                '& th, & td': {
                  padding: tokens.aliasesSpaceComponentDefault,
                  borderBottom: `1px solid ${tokens.aliasesColorBorderStaticContainerSubtle}`,
                  textAlign: 'left',
                  verticalAlign: 'top',
                },
                '& th': {
                  width: 180,
                  fontWeight: 'normal',
                },
              }}
            >
              <tbody>
                <tr>
                  <th><Typography variant="captionNeutral">Correlation ID</Typography></th>
                  <td>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {data.correlation_id}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <th><Typography variant="captionNeutral">Timestamp</Typography></th>
                  <td><Typography variant="body1">{fmtDate(data.started_at)}</Typography></td>
                </tr>
                <tr>
                  <th><Typography variant="captionNeutral">Duration</Typography></th>
                  <td>
                    <Typography variant="body1">
                      {data.duration_ms != null ? `${data.duration_ms} ms` : '—'}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <th><Typography variant="captionNeutral">Account</Typography></th>
                  <td>
                    <Typography variant="body1">{data.account_name}</Typography>
                    <Typography variant="captionSubtle" sx={{ fontFamily: 'monospace' }}>
                      {data.account_id}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <th><Typography variant="captionNeutral">Provider / Model</Typography></th>
                  <td>
                    <Typography variant="body1">
                      {data.provider || '—'} / {data.model || '—'}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <th><Typography variant="captionNeutral">Region</Typography></th>
                  <td><Typography variant="body1">{data.region || '—'}</Typography></td>
                </tr>
                <tr>
                  <th><Typography variant="captionNeutral">Content Type</Typography></th>
                  <td><Typography variant="body1">{data.content_type || '—'}</Typography></td>
                </tr>
                <tr>
                  <th><Typography variant="captionNeutral">Content Size</Typography></th>
                  <td>
                    <Typography variant="body1">
                      {data.content_size_bytes != null
                        ? `${data.content_size_bytes.toLocaleString()} bytes`
                        : '—'}
                    </Typography>
                  </td>
                </tr>
                {data.error_code && (
                  <tr>
                    <th><Typography variant="captionNeutral">Error</Typography></th>
                    <td>
                      <Typography variant="body1" sx={{ color: tokens.aliasesColorTextError }}>
                        {data.error_code}: {data.error_message}
                      </Typography>
                    </td>
                  </tr>
                )}
              </tbody>
            </Box>

            {data.findings && data.findings.length > 0 && (
              <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault }}>
                <Typography variant="h4">
                  Findings ({data.findings.length})
                </Typography>
                <Box
                  component="table"
                  sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    backgroundColor: tokens.aliasesColorBgStaticContainerSubtle,
                    '& th, & td': {
                      padding: tokens.aliasesSpaceComponentDefault,
                      borderBottom: `1px solid ${tokens.aliasesColorBorderStaticContainerSubtle}`,
                      textAlign: 'left',
                    },
                  }}
                >
                  <thead>
                    <tr>
                      <th><Typography variant="body1Bold">Source</Typography></th>
                      <th><Typography variant="body1Bold">Type</Typography></th>
                      <th><Typography variant="body1Bold">Category</Typography></th>
                      <th><Typography variant="body1Bold">Confidence</Typography></th>
                      <th><Typography variant="body1Bold">Severity</Typography></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.findings.map((f, idx) => (
                      <tr key={idx}>
                        <td><Typography variant="body1">{f.source || '—'}</Typography></td>
                        <td><Typography variant="body1">{f.type || '—'}</Typography></td>
                        <td><Typography variant="body1">{f.category || '—'}</Typography></td>
                        <td>
                          <Typography variant="body1">
                            {f.confidence != null ? `${(f.confidence * 100).toFixed(0)}%` : '—'}
                          </Typography>
                        </td>
                        <td><Typography variant="body1">{f.severity || '—'}</Typography></td>
                      </tr>
                    ))}
                  </tbody>
                </Box>
              </Stack>
            )}

            {data.prompt_stored && (
              <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault }}>
                <Typography variant="h4">Prompt</Typography>
                <Box
                  sx={{
                    backgroundColor: tokens.aliasesColorBgStaticContainerNeutral,
                    padding: tokens.aliasesSpaceContainerDefault,
                    borderRadius: tokens.aliasesBorderRadiusDefault,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: 300,
                    overflow: 'auto',
                  }}
                >
                  {data.prompt_stored}
                </Box>
              </Stack>
            )}

            {data.backends && (
              <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault }}>
                <Typography variant="h4">Backends</Typography>
                <Box
                  component="table"
                  sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    '& th, & td': {
                      padding: tokens.aliasesSpaceComponentDefault,
                      borderBottom: `1px solid ${tokens.aliasesColorBorderStaticContainerSubtle}`,
                      textAlign: 'left',
                    },
                    '& th': {
                      width: 150,
                    },
                  }}
                >
                  <tbody>
                    {Object.entries(data.backends).map(([name, info]) => (
                      <tr key={name}>
                        <th><Typography variant="captionNeutral">{name}</Typography></th>
                        <td>
                          <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                            {info.url || '—'}
                          </Typography>
                          {info.source && (
                            <Typography variant="captionSubtle">
                              Source: {info.source}
                            </Typography>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Box>
              </Stack>
            )}
          </Stack>
        </Card>
      )}
    </Box>
  );
};

export default ScanLookupContainer;
